import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PageOptimizationService } from '../../../../../../services/page-optimization.service';
import { TaskFeedbackService } from '../../../../../../services/task-feedback.service';
import { TaskNoteService } from '../../../../../../services/task-note.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import CustomValidators from '../../../../../../shared/custom-validators';
import { TaskNoteTypes } from 'app/models/enums/TaskNoteTypes';
import { catchError, finalize, forkJoin, of, map, Observable, Subject, takeUntil, throwError } from 'rxjs';
import TaskNote = Boo.Objects.TaskNote;
import TaskFeedback = Boo.Objects.TaskFeedback;
import { SaveTypes } from 'app/models/enums/SaveTypes';

@Component({
  selector: 'app-components-specialist-actioncomponents-page-optimization-shared-note',
  templateUrl: './note.component.html'
})
export class PageOptimizationNoteComponent implements OnInit, OnDestroy {
  @Input() userId: number;
  @Input() taskId: number;
  @Input() label: number;
  @Input() noteType: TaskNoteTypes;
  @Input() createFeedback = false;
  @Output() noteChanged = new EventEmitter<void>();

  formGroup: FormGroup;
  noteControl: FormControl<string>;

  isLoading = true;
  isRequired = false;
  taskNote: TaskNote;
  taskFeedback: TaskFeedback;

  private destroy$ = new Subject<void>();

  constructor(
    private taskFeedbackService: TaskFeedbackService,
    private taskNoteService: TaskNoteService,
    private pageOptimizationService: PageOptimizationService) { }

  ngOnInit(): void {
    forkJoin([
      this.taskNoteService.getByTaskId(this.taskId),
      this.createFeedback ? this.pageOptimizationService.getLatestContentTaskId(this.taskId) : of(0)
    ])
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: ([taskNotes, latestContentTaskId]) => {
          const mostRecentNote = taskNotes.sort((a, b) => new Date(b.InsertedDate).getTime() - new Date(a.InsertedDate).getTime())[0];

          this.taskNote = mostRecentNote && mostRecentNote.TaskId === this.taskId && mostRecentNote.TaskNoteTypeId === this.noteType ? mostRecentNote : this.createTaskNote();

          if (this.createFeedback) {
            this.taskFeedback = this.createTaskFeedback(latestContentTaskId);
          }

          this.setupForm();
        },
        error: (err) => toastr.error('Failed to load note:', err)
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Some components in Page Optimization are not required to be valid for SaveTypes.Save but note must always be valid
  isValid(): boolean {
    this.validateAllControls(false);

    if (!this.formGroup.valid) {
      this.validateAllControls(true);
      return false;
    }

    return true;
  }

  save(saveType: SaveTypes): Observable<void> {
    if (!this.isValid()) {
      return of(void 0);
    }

    const note = this.noteControl.value.trim();

    if (note === '') {
      return this.taskNote.TaskNoteId ?
        this.taskNoteService.delete(this.taskNote.TaskNoteId)
        .pipe(
          map(() => void 0),
          catchError(err => {
            toastr.error(`Failed to delete ${this.label}:`, err);
            return throwError(() => err);
          })
        ) : of(void 0);
    }

    this.taskNote.Note = note;
    this.taskNote.FromUserId = this.userId;
    this.taskNote.InsertedDate = moment().toDate();

    const saveFeedback = [SaveTypes.Complete, SaveTypes.Reject].includes(saveType) && this.createFeedback;
    if (saveFeedback) {
      this.taskFeedback.Feedback = this.taskNote.Note;
      this.taskFeedback.Passed = (saveType === SaveTypes.Complete);
    }

    return forkJoin([
      this.taskNoteService.save(this.taskNote),
      saveFeedback ? this.taskFeedbackService.save(this.taskFeedback) : of(null)
    ])
    .pipe(
      map(() => void 0),
      catchError(err => {
        toastr.error(`Failed to save ${this.label}:`, err);
        return throwError(() => err);
      })
    );
  }

  private setupForm(): void {
    this.noteControl = new FormControl(this.taskNote.Note, [
      Validators.maxLength(4000),
      CustomValidators.requiredIfValidator(() => this.isRequired)
    ]);
    this.formGroup = new FormGroup({
      noteControl: this.noteControl
    });

    setTimeout(() => {
      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.noteChanged.emit();
        });
    }, 10);
  }

  private createTaskNote(): TaskNote {
    return {
      FromUserFullName: '',
      FromUserId: this.userId,
      InsertedDate: null,
      Note: '',
      TaskId: this.taskId,
      TaskNoteTypeId: this.noteType as number,
      TaskNoteId: 0,
      TaskTypeEnum: null,
      TaskTypeName: '',
      ToUserFullName: '',
      ToUserId: 0
    }
  }

  private createTaskFeedback(taskId: number): TaskFeedback {
    return {
      TaskId: taskId, // Task getting feedback
      FeedbackTaskId: this.taskId, // Task providing the feedback
      Passed: false,
      Feedback: '',
      TaskFeedbackId: 0,
      Task: null,
      FeedbackTask: null,
      TaskHistoryInfo: null
    };
  }

  private validateAllControls(emitEvent: boolean) : void {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].markAsDirty();
      this.formGroup.controls[key].updateValueAndValidity({ emitEvent: emitEvent });
    });
  }
}
